import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const closestSection = this.closest('.shopify-section, dialog, quick-add-dialog') || document;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = async (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('product-card') && !event.target.closest('quick-add-dialog, dialog')) return;

    try {
      const { detail } = await event.promise;
      if (!detail?.html || this.#abortController?.signal.aborted) return;

      const { html, newProduct } = detail;

      if (newProduct) {
        this.dataset.productId = newProduct.id;
      } else if (detail.productId && detail.productId !== this.dataset.productId) {
        return;
      }

      const { priceContainer, volumePricingNote } = this.refs;
      // Find the new product-price element in the updated HTML
      const newProductPrice =
        html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`) ||
        html.querySelector('product-price');
      if (!newProductPrice) return;

      // Update price container
      const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
      if (newPrice && priceContainer) {
        priceContainer.replaceWith(newPrice);
      }

      // Update volume pricing note
      const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
      if (!newNote) {
        volumePricingNote?.remove();
      } else if (!volumePricingNote) {
        // Use newPrice since priceContainer was just replaced and now points to the detached element
        newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
      } else {
        volumePricingNote.replaceWith(newNote);
      }

      // Update tax note
      const existingTaxNote = this.querySelector('.tax-note');
      const newTaxNote = newProductPrice.querySelector('.tax-note');
      if (newTaxNote) {
        if (existingTaxNote) {
          existingTaxNote.replaceWith(newTaxNote);
        } else {
          this.appendChild(newTaxNote);
        }
      } else if (existingTaxNote) {
        existingTaxNote.remove();
      }

      // Update installments (SPI banner) variant ID to trigger payment terms re-render
      const existingInstallments = this.querySelector('.installments');
      const newInstallments = newProductPrice.querySelector('.installments');
      if (newInstallments) {
        if (existingInstallments) {
          existingInstallments.replaceWith(newInstallments);
        } else {
          this.appendChild(newInstallments);
        }
      } else if (existingInstallments) {
        existingInstallments.remove();
      }

      const installmentsInput = /** @type {HTMLInputElement|null} */ (
        this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
      );
      if (installmentsInput) {
        installmentsInput.value = detail.resource?.id ?? '';
        installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[product-price] Event promise rejected:', error);
      }
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
